"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { ChevronsLeftRight, Sparkles, Calendar, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
const lookbookItems = [{
  id: "item-1",
  type: "standard",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/bespoke_kitchen_renovation_london.png",
  title: "Bespoke Chef's Kitchen",
  description: "A premium open-plan kitchen featuring hand-crafted cabinetry and integrated appliances. This bespoke finish adds significant character and functionality to the home.",
  stylist: "James Wilson",
  service: "Kitchen Renovation",
  aspectRatio: "aspect-[3/4]"
}, {
  id: "item-2",
  type: "before-after",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/victorian_terrace_kitchen_before.png",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/open_plan_glass_extension_after.png",
  title: "30% More Natural Light",
  description: "We transformed a cramped Victorian kitchen into a light-filled masterpiece by installing structural glass and high-performance bi-fold doors.",
  stylist: "James Wilson",
  service: "Open-Plan Conversion",
  aspectRatio: "aspect-[4/5]"
}, {
  id: "item-3",
  type: "standard",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/bifold_doors_installation_london.png",
  title: "Signature Bi-Fold Install",
  description: "Seamlessly merging indoor luxury with garden serenity. Our ultra-slim frames maximize views and ensure year-round thermal efficiency.",
  stylist: "Sarah Hughes",
  service: "Glass Installation",
  aspectRatio: "aspect-square"
}, {
  id: "item-4",
  type: "before-after",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/cramped_kitchen_remodel_before.png",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/luxury_modern_living_after.png",
  title: "+20% Property Value",
  description: "A strategic rear extension and internal remodel that significantly increased the property's market value while creating a stunning living space.",
  stylist: "James Wilson",
  service: "Full Renovation",
  aspectRatio: "aspect-[3/5]"
}, {
  id: "item-5",
  type: "standard",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/skylight_dining_room_extension.png",
  title: "The Sky-Light Dining",
  description: "Architectural glazing meets traditional London brickwork. This project focused on bringing the sky into the heart of the home.",
  stylist: "Sarah Hughes",
  service: "Modern Extension",
  aspectRatio: "aspect-[4/3]"
}, {
  id: "item-6",
  type: "standard",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/modern_web_room_bathroom_design.png",
  title: "Heritage Suite Revival",
  description: "Modernizing Edwardian features with premium fixtures and bespoke tiling for a timeless, high-end bathroom experience.",
  stylist: "James Wilson",
  service: "Bathroom Renovation",
  aspectRatio: "aspect-[2/3]"
}, {
  id: "item-7",
  type: "standard",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613388/img/skykight_roof_lantern.png",
  title: "Skylight Roof Lantern",
  description: "Elegant roof lantern bringing natural light and showcasing our roof lantern and skylight services.",
  stylist: "Sarah Hughes",
  service: "Roof Lantern Installation",
  aspectRatio: "aspect-square"
}];
function BeforeAfterSlider({
  item,
  onClick
}: {
  item: any;
  onClick: () => void;
}) {
  const [sliderPos, setSliderPos] = useState(50);
  return <div className={`relative w-full overflow-hidden rounded-sm group cursor-pointer ${item.aspectRatio} bg-slate-200`} onClick={onClick}>
      <img src={item.imageAfter} alt={`${item.title} After`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />

      <div className="absolute inset-0 w-full h-full overflow-hidden grayscale brightness-75" style={{
      clipPath: `inset(0 ${100 - sliderPos}% 0 0)`
    }}>
        <img src={item.imageBefore} alt={`${item.title} Before`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
      </div>

      <div className="absolute top-0 bottom-0 w-[1px] bg-white/50 shadow-[0_0_20px_rgba(0,0,0,0.5)] z-10 pointer-events-none transition-all duration-100" style={{
      left: `${sliderPos}%`
    }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-[#0a1128]/90 backdrop-blur-lg rounded-full flex items-center justify-center shadow-2xl border border-[#b4975a]/40">
          <ChevronsLeftRight className="w-6 h-6 text-[#b4975a]" />
        </div>
      </div>

      <input type="range" min="0" max="100" value={sliderPos} onChange={e => setSliderPos(Number(e.target.value))} onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20" aria-label="Toggle before and after view" />

      <div className="absolute bottom-4 left-4 px-4 py-1.5 bg-[#0a1128]/80 backdrop-blur-md rounded-sm text-[10px] text-white font-semibold tracking-widest uppercase z-10 pointer-events-none border border-white/10">
        Before
      </div>
      <div className="absolute bottom-4 right-4 px-4 py-1.5 bg-[#b4975a]/90 backdrop-blur-md rounded-sm text-[10px] text-[#0a1128] font-semibold tracking-widest uppercase z-10 pointer-events-none border border-[#b4975a]/20">
        BVP After
      </div>
    </div>;
}
function StandardImage({
  item,
  onClick
}: {
  item: any;
  onClick: () => void;
}) {
  return <div className={`relative w-full overflow-hidden rounded-sm group cursor-pointer ${item.aspectRatio} bg-slate-200`} onClick={onClick}>
      <img src={item.image} alt={item.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1128]/90 via-[#0a1128]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-8">
        <h3 className="text-2xl font-serif text-white mb-2 tracking-tight">{item.title}</h3>
        <p className="text-xs text-[#b4975a] flex items-center gap-2 font-medium tracking-widest uppercase">
          <Sparkles className="w-3 h-3" /> View Project Details
        </p>
      </div>
    </div>;
}
export default function TheLookbook() {
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleOpenDialog = (item: any) => {
    setSelectedItem(item);
    setDialogOpen(true);
  };
  return <section id="the-london-transformation" data-section-id="147894" className="bg-background text-foreground py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[40rem] h-[40rem] bg-[#b4975a]/5 rounded-full blur-[120px] -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[50rem] h-[50rem] bg-[#0a1128]/5 rounded-full blur-[150px] translate-y-1/2 pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-[#b4975a]/10 border border-[#b4975a]/20 text-[#b4975a] text-xs font-bold tracking-[0.2em] uppercase">
            The London Transformation
          </div>
          <h2 className="text-5xl md:text-6xl font-serif font-semibold mb-6 tracking-tight text-[#0a1128]">
            Architectural Mastery
          </h2>
          <p className="text-slate-600 text-xl leading-relaxed font-sans">
            Specialists in opening up homes across London to create light-filled, open-plan living spaces. Explore our portfolio of bespoke transformations.
          </p>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-8 space-y-8">
          {lookbookItems.map((item, i) => <div key={item.id} data-index={i} className="break-inside-avoid">
              {item.type === "before-after" ? <BeforeAfterSlider item={item} onClick={() => handleOpenDialog(item)} /> : <StandardImage item={item} onClick={() => handleOpenDialog(item)} />}
            </div>)}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent showCloseButton={false} className="max-w-6xl w-[95vw] h-[90vh] p-0 overflow-hidden bg-white/95 backdrop-blur-3xl border-[#b4975a]/10 shadow-2xl flex flex-col md:flex-row rounded-sm">
          <Button variant="ghost" size="icon" onClick={() => setDialogOpen(false)} className="absolute top-6 right-6 z-50 bg-white/80 hover:bg-white backdrop-blur-md rounded-full border border-slate-200 shadow-sm" aria-label="Close dialog">
            <X className="w-5 h-5 text-[#0a1128]" />
          </Button>

          <div className="w-full md:w-3/5 h-1/2 md:h-full relative bg-slate-100 flex items-center justify-center overflow-hidden">
            {selectedItem?.type === "before-after" ? <div className="w-full h-full relative">
                <BeforeAfterSlider item={selectedItem} onClick={() => {}} />
              </div> : <img src={selectedItem?.image} alt={selectedItem?.title} className="w-full h-full object-cover" />}
          </div>

          <div className="w-full md:w-2/5 h-1/2 md:h-full p-8 md:p-16 flex flex-col justify-center overflow-y-auto bg-white">
            <DialogHeader className="text-left mb-8 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#b4975a]/10 text-[#b4975a] text-xs font-bold tracking-widest uppercase w-fit">
                <Sparkles className="w-4 h-4" />
                {selectedItem?.service}
              </div>
              <DialogTitle className="text-5xl md:text-6xl font-serif text-[#0a1128] leading-tight font-semibold">
                {selectedItem?.title}
              </DialogTitle>
              <DialogDescription className="text-xl text-slate-600 leading-relaxed font-sans">
                {selectedItem?.description}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-10 mt-6">
              <div className="flex items-center gap-5 p-6 rounded-sm bg-slate-50 border border-slate-100">
                <div className="w-14 h-14 rounded-full bg-[#0a1128] flex items-center justify-center text-xl font-serif text-[#b4975a]">
                  {selectedItem?.stylist.charAt(0)}
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-bold tracking-widest uppercase">Project Lead</p>
                  <p className="font-serif text-2xl text-[#0a1128]">{selectedItem?.stylist}</p>
                </div>
              </div>

              <div className="pt-10 border-t border-slate-100 flex flex-col gap-4">
                <Button asChild size="lg" className="w-full text-sm font-bold tracking-[0.2em] uppercase h-16 rounded-sm bg-[#b4975a] text-white hover:bg-[#a38649] transition-all duration-300 shadow-lg shadow-[#b4975a]/20 group">
                  <Link to="#">
                    <SafeHydrate><Calendar className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" /></SafeHydrate>
                    Book Free Consultation
                  </Link>
                </Button>
                
                <Button variant="outline" asChild size="lg" className="w-full text-sm font-bold tracking-[0.2em] uppercase h-16 rounded-sm border-[#0a1128] text-[#0a1128] hover:bg-[#0a1128] hover:text-white transition-all duration-300 group">
                  <Link to="https://wa.me/your-number" newTab>
                    <MessageCircle className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" />
                    WhatsApp Us
                  </Link>
                </Button>

                <p className="text-center text-xs font-medium text-slate-400 mt-2 tracking-wide">
                  Schedule a visit or chat with us about your London property transformation.
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>;
}